import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { SnackbarContext } from './userContext';
import { Loading } from './Loading';
import { SearchFilterBar } from './SearchFilterBar';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const trim = (s, n) => (s && s.length > n ? s.substring(0, n) + '…' : s || '');

/* ─────────── Inline icon components ─────────── */

const IconPin = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
    <circle cx="12" cy="9" r="3" />
  </svg>
);
const IconArrowRight = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const IconSearchOff = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
    <path d="M8 14l6-6M14 14l-6-6" />
  </svg>
);

/* ─────────── Hero card ─────────── */

function ResultsHero({ kicker, title, count, location, similarMatches, notFound }) {
  return (
    <section
      className="
        relative isolate overflow-hidden mt-3 mb-6
        rounded-2xl border border-border-subtle
        bg-gradient-to-br from-surface-paper to-surface-elevated
      "
    >
      {/* layered bg */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(900px 400px at 10% -10%, rgba(251,191,36,0.18), transparent 60%), radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.08), transparent 60%)' }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
        }}
      />
      {/* amber gradient line at top */}
      <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center px-5 sm:px-7 md:px-9 py-7 sm:py-9">

        {/* LEFT - kicker + title + amber line + meta */}
        <div className="min-w-0">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
            <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber">{kicker}</span>
          </div>

          <h1 className="text-[28px] sm:text-[36px] md:text-[42px] font-bold leading-[1.04] tracking-[-0.03em] text-fg-primary">
            {title}
          </h1>

          <div aria-hidden className="mt-4 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13.5px]">
            {location && (
              <span className="inline-flex items-center gap-1.5 text-fg-secondary">
                <IconPin className="h-3.5 w-3.5 text-amber" />
                <span className="font-medium text-fg-primary">{trim(location, 40)}</span>
              </span>
            )}
            {(similarMatches || notFound) && (
              <span className="text-[12.5px] italic text-fg-tertiary">- showing similar matches</span>
            )}
          </div>
        </div>

        {/* RIGHT - prominent count number */}
        <div
          className="
            flex items-baseline gap-3
            md:flex-col md:items-end md:gap-1
            md:border-l md:border-border-subtle/60
            md:pl-8 lg:pl-10
            shrink-0
          "
        >
          <div
            className="
              text-[56px] sm:text-[68px] md:text-[78px] lg:text-[88px]
              font-extrabold leading-none tracking-[-0.05em]
              text-amber
            "
            style={{ textShadow: '0 0 40px rgba(251,191,36,0.25)' }}
          >
            {count}
          </div>
          <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-fg-tertiary">
            {count === 1 ? 'listing' : 'listings'}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Product card (regular search results) ─────────── */

function ProductCard({ item }) {
  return (
    <RouterLink
      to={`/psdetails/${item.product_slug}/${item.product_id}`}
      title={item.product_name}
      className="
        group relative flex h-full flex-col overflow-hidden rounded-xl
        border border-border-subtle bg-surface-paper
        transition-[transform,border-color,box-shadow] duration-300 ease-out-soft
        hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong
      "
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-surface-elevated">
        <img
          src={item.product_image}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out-soft group-hover:scale-[1.07]"
        />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-500 ease-out-soft group-hover:opacity-60" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-out-soft group-hover:opacity-100"
          style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 100%, rgba(251,191,36,0.16), transparent 70%)' }}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber transition-colors duration-200 ease-out-soft group-hover:text-amber-light">
          {trim(item.sub_category_name, 30)}
        </span>

        <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-fg-primary line-clamp-2 transition-transform duration-300 ease-out-soft group-hover:-translate-y-0.5">
          {trim(item.product_name, 40)}
        </h3>

        <div className="mt-auto flex items-center gap-1.5 pt-1.5 text-[12px] text-fg-tertiary">
          <IconPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{trim(item.product_location_text, 40)}</span>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <span
            aria-hidden
            className="block h-[2px] w-5 bg-amber transition-[width,background-color] duration-500 ease-out-soft group-hover:w-10 group-hover:bg-amber-light"
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber opacity-0 -translate-x-1 transition-[opacity,transform] duration-300 ease-out-soft group-hover:opacity-100 group-hover:translate-x-0">
            View
          </span>
        </div>
      </div>
    </RouterLink>
  );
}

/* ─────────── Engineer / Workforce card - minimal, name-led ─────────── */

function EwCard({ item }) {
  const fullName = trim(`${item.fname || ''} ${item.lname || ''}`.trim(), 30);
  const role = trim(`${item.subcat || ''} · ${item.cat || ''}`, 40);
  return (
    <RouterLink
      to={`/ewdetails/${item?.cat}/${item?.user_id}`}
      className="
        group relative overflow-hidden rounded-xl
        border border-border-subtle bg-surface-paper
        pl-6 pr-5 py-5
        transition-[transform,border-color,box-shadow] duration-300 ease-out-soft
        hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong
      "
    >
      {/* Left edge amber accent - short by default, full-height on hover */}
      <span
        aria-hidden
        className="
          pointer-events-none absolute left-0 top-1/2 -translate-y-1/2
          w-[3px] h-8 rounded-r-full
          bg-amber
          transition-[height,opacity] duration-300 ease-out-soft
          group-hover:h-full group-hover:top-0 group-hover:translate-y-0 group-hover:rounded-none
        "
      />

      {/* Top row - role kicker + arrow */}
      <div className="flex items-start justify-between gap-3">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber leading-none truncate">
          {role}
        </span>
        <IconArrowRight
          className="
            h-4 w-4 shrink-0 text-fg-tertiary
            transition-[color,transform] duration-300 ease-out-soft
            group-hover:text-amber group-hover:translate-x-1
          "
        />
      </div>

      {/* Name */}
      <h3
        className="
          mt-3 text-[18px] font-semibold leading-[1.2] tracking-[-0.015em] text-fg-primary
          truncate
          transition-colors duration-200 ease-out-soft
          group-hover:text-white
        "
      >
        {fullName}
      </h3>

      {/* Location */}
      <div className="mt-2 flex items-center gap-1.5 text-[12.5px] text-fg-tertiary">
        <IconPin className="h-3 w-3 shrink-0" />
        <span className="truncate">{trim(item.area_ids, 40)}</span>
      </div>
    </RouterLink>
  );
}

/* ─────────── Empty state ─────────── */

function EmptyResults() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border-subtle bg-surface-paper/40 px-6 py-14 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-subtle bg-surface-overlay/60 text-fg-tertiary">
        <IconSearchOff className="h-6 w-6" />
      </div>
      <h3 className="text-[16px] font-semibold text-fg-primary">No results found</h3>
      <p className="mt-1.5 max-w-[380px] text-[13px] leading-relaxed text-fg-secondary">
        Try a wider location, a different category, or clearing the keyword.
      </p>
    </div>
  );
}

/* ═══════════════════ Component ═══════════════════ */

export const SearchResults = () => {
  const paramData = useParams();
  const history = useNavigate();
  const { setSnack } = useContext(SnackbarContext);

  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [slides, setSlides] = useState([]);
  const [cats, setCats] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [metaData, setMetaData] = useState(null);
  const [resultsNotFound, setResultsNotFound] = useState(false);

  const [loc, setLoc] = useState('');
  const [cat, setCat] = useState('0');
  const [subCat, setSubcat] = useState('0');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(0);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    const locn = String(paramData.locn);
    const catp = parseInt(paramData.catn);
    const subcatp = parseInt(paramData.subcat);
    const searchp = String(paramData.searchn);

    const formData = {};
    if (catp === 0 || isNaN(catp)) {
      history('/');
      return;
    }
    formData.cat = String(catp);
    setCat(String(catp));

    if (subcatp !== 0 && !isNaN(subcatp)) {
      formData.subcat = String(subcatp);
      setSubcat(String(subcatp));
    }
    if (String(locn) !== 'undefined') {
      formData.loc = String(locn);
      setLoc(String(locn));
    }
    if (String(searchp) !== 'undefined') {
      formData.search = String(searchp);
      setSearch(String(searchp));
    }

    Axios.post('/new_apis/get-search-data', formData, { headers: { key } }).then((res) => {
      if (res.data.status === 1) {
        setSlides(res.data.slides || []);
        setCats(res.data.cats || []);
        setMetaData(res.data.metadata);
        setSubcats(res.data.subcats || []);
        setSearchData(res.data.data || []);
        setResultsNotFound(res.data.notfound);
        setLoading(false);
      } else if (res.data.status === 0) {
        history('/');
      }
    });
  }, [paramData, history]);

  const subcatOptions = subcats.filter((sc) => sc.category_ids === cat);

  const apply = (e) => {
    if (e) e.preventDefault();
    setError(0);
    if (!loc) {
      setError(1);
      setSnack({ message: 'Please enter your location.', type: 'error', open: true });
      return;
    }
    if (cat === '0') {
      setError(2);
      setSnack({ message: 'Please select a category.', type: 'error', open: true });
      return;
    }
    history(`/searchresults/${loc}/${cat}/${subCat}/${search}`);
  };

  if (loading) return <Loading />;

  const isEngWf = metaData.category_id === '6' || metaData.category_id === '7';

  // Dedup by unique key - guards against backend sending duplicates and avoids
  // React duplicate-key warnings.
  const keyField = isEngWf ? 'user_id' : 'product_id';
  const seen = new Set();
  const uniqueResults = searchData.filter((item) => {
    const k = item[keyField];
    if (k == null || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const count = uniqueResults.length;

  return (
    <>
      {metaData && (
        <Helmet>
          <title>{metaData.seo_title} - Search Results | ESAY</title>
          <meta name="description" content={metaData.seo_desc} />
          <meta name="keywords" content={metaData.seo_keywords} />
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <ResultsHero
          kicker={resultsNotFound ? 'No exact match' : 'Search results'}
          title={metaData.category_name}
          count={count}
          location={loc}
          similarMatches={resultsNotFound && count > 0}
          notFound={resultsNotFound && count === 0}
        />

        <SearchFilterBar
          loc={loc} setLoc={setLoc}
          cat={cat} setCat={setCat}
          subCat={subCat} setSubcat={setSubcat}
          search={search} setSearch={setSearch}
          cats={cats}
          subcatOptions={subcatOptions}
          error={error}
          onSubmit={apply}
          buttonLabel="Apply"
        />

        <section className="mt-8 mb-10">
          {count === 0 && <EmptyResults />}

          {count > 0 && !isEngWf && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {uniqueResults.map((item) => (
                <ProductCard key={item.product_id} item={item} />
              ))}
            </div>
          )}

          {count > 0 && isEngWf && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {uniqueResults.map((item) => (
                <EwCard key={item.user_id} item={item} />
              ))}
            </div>
          )}
        </section>
      </PageContainer>
    </>
  );
};
